import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Link2, UserCheck, UserPlus, Users } from 'lucide-react';
import {
  Button,
  Card,
  Description,
  Heading,
  SearchField,
  Spinner,
  useOverlayState,
} from '@heroui/react';
import { ConfirmDialog } from '../../../components/ui/confirm-dialog';
import { createFirebaseUser } from '../../../api/account';
import {
  createMembership,
  deleteMembership,
  updateMembership,
  type CreateMembershipPayload,
} from '../../../api/memberships';
import { listUsers, type UserMembership, type UserWithMemberships } from '../../../api/users';
import { PageHeader } from '../../../components/page/page-header';
import { sendPasswordReset } from '../../../lib/firebase';
import { showErrorFromUnknown, showSuccess } from '../../../lib/toast';
import { AssociateMembershipDrawer } from '../components/associate-membership-drawer';
import { InviteUserDrawer } from '../components/invite-user-drawer';
import { MembershipFormDrawer } from '../components/membership-form-drawer';
import { UserCard } from '../components/user-card';
import { UserDetailPanel } from '../components/user-detail-panel';
import { UserListTable } from '../components/user-list-table';
import { getUserDisplayName, matchesUserSearch } from '../utils/user-labels';

const QUERY_KEY = ['admin', 'users'] as const;

function StatCard({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Users;
  label: string;
  value: number;
}) {
  return (
    <Card>
      <Card.Content className="flex items-center gap-3 p-4">
        <div className="flex size-10 items-center justify-center rounded-full bg-accent-soft text-accent">
          <Icon size={20} />
        </div>
        <div>
          <Description>{label}</Description>
          <Heading level={3} className="text-2xl font-semibold">
            {value}
          </Heading>
        </div>
      </Card.Content>
    </Card>
  );
}

export default function AdminUsersPage() {
  const queryClient = useQueryClient();
  const inviteDrawer = useOverlayState();
  const associateDrawer = useOverlayState();
  const editMembershipDrawer = useOverlayState();
  const deleteModal = useOverlayState();

  const [search, setSearch] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [associatingUser, setAssociatingUser] = useState<UserWithMemberships | null>(null);
  const [editingMembership, setEditingMembership] = useState<{
    membership: UserMembership;
    user: UserWithMemberships;
  } | null>(null);
  const [deletingMembership, setDeletingMembership] = useState<{
    membership: UserMembership;
    user: UserWithMemberships;
  } | null>(null);

  const { data: users = [], isLoading, isError, refetch } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: listUsers,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: QUERY_KEY });

  const inviteMutation = useMutation({
    mutationFn: async (payload: { email: string; password: string; displayName?: string }) => {
      await createFirebaseUser(payload);
      await sendPasswordReset(payload.email);
    },
    onSuccess: async () => {
      await invalidate();
      showSuccess('Convite enviado. O usuário receberá um email para definir a senha.');
    },
    onError: (err) => showErrorFromUnknown(err),
  });

  const updateMembershipMutation = useMutation({
    mutationFn: ({
      membershipId,
      payload,
    }: {
      membershipId: string;
      payload: { role?: UserMembership['role']; status?: UserMembership['status'] };
    }) => updateMembership(membershipId, payload),
    onSuccess: async () => {
      await invalidate();
      showSuccess('Associação atualizada com sucesso.');
    },
    onError: (err) => showErrorFromUnknown(err),
  });

  const deleteMembershipMutation = useMutation({
    mutationFn: deleteMembership,
    onSuccess: async () => {
      await invalidate();
      showSuccess('Associação removida com sucesso.');
    },
    onError: (err) => showErrorFromUnknown(err),
  });

  const createMembershipMutation = useMutation({
    mutationFn: (payload: CreateMembershipPayload) => createMembership(payload),
    onSuccess: async () => {
      await invalidate();
      showSuccess('Usuário associado ao condomínio com sucesso.');
    },
    onError: (err) => showErrorFromUnknown(err),
  });

  const filtered = useMemo(
    () => users.filter((user) => matchesUserSearch(user, search)),
    [users, search],
  );

  const selectedUser = useMemo(
    () => filtered.find((user) => user.id === selectedUserId) ?? null,
    [filtered, selectedUserId],
  );

  useEffect(() => {
    setSelectedUserId((current) => {
      if (filtered.length === 0) return null;
      if (current && filtered.some((user) => user.id === current)) return current;
      return filtered[0]!.id;
    });
  }, [filtered]);

  const activeUsersCount = useMemo(
    () => users.filter((user) => user.memberships.some((m) => m.status === 'ACTIVE')).length,
    [users],
  );

  const totalMemberships = useMemo(
    () => users.reduce((acc, user) => acc + user.memberships.length, 0),
    [users],
  );

  const openAssociate = (user: UserWithMemberships) => {
    setAssociatingUser(user);
    associateDrawer.open();
  };

  const openEditMembership = (membership: UserMembership, user: UserWithMemberships) => {
    setEditingMembership({ membership, user });
    editMembershipDrawer.open();
  };

  const openDeleteMembership = (membership: UserMembership, user: UserWithMemberships) => {
    setDeletingMembership({ membership, user });
    deleteModal.open();
  };

  const handleConfirmDelete = async () => {
    if (!deletingMembership) return;
    await deleteMembershipMutation.mutateAsync(deletingMembership.membership.id);
    setDeletingMembership(null);
  };

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader
          title="Usuários"
          description="Gerencie usuários cadastrados e suas associações aos condomínios."
        />
        <div className="flex h-64 items-center justify-center">
          <Spinner size="lg" />
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader
          title="Usuários"
          description="Gerencie usuários cadastrados e suas associações aos condomínios."
        />
        <Card className="max-w-md">
          <Card.Content className="flex flex-col items-center gap-4 py-10 text-center">
            <AlertCircle className="text-danger" size={40} />
            <Description>Não foi possível carregar os usuários.</Description>
            <Button variant="primary" onPress={() => void refetch()}>
              Tentar novamente
            </Button>
          </Card.Content>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Usuários"
        description="Gerencie usuários cadastrados e suas associações aos condomínios."
      />

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <SearchField
          aria-label="Pesquisar usuários"
          value={search}
          onChange={setSearch}
          className="w-full sm:max-w-md"
        >
          <SearchField.Group>
            <SearchField.SearchIcon />
            <SearchField.Input placeholder="Pesquisar por nome, email ou CPF…" />
            <SearchField.ClearButton />
          </SearchField.Group>
        </SearchField>
        <Button variant="primary" onPress={inviteDrawer.open}>
          <UserPlus size={18} />
          Convidar usuário
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <StatCard icon={Users} label="Total de usuários" value={users.length} />
        <StatCard icon={UserCheck} label="Usuários com associação ativa" value={activeUsersCount} />
        <StatCard icon={Link2} label="Total de associações" value={totalMemberships} />
      </div>

      {filtered.length === 0 ? (
        <Card>
          <Card.Content className="py-12 text-center">
            <Description>
              {search.trim()
                ? 'Nenhum usuário encontrado para sua busca.'
                : 'Nenhum usuário cadastrado ainda.'}
            </Description>
          </Card.Content>
        </Card>
      ) : (
        <>
          <div className="flex flex-col gap-4 md:hidden">
            {filtered.map((user) => (
              <UserCard
                key={user.id}
                user={user}
                onAssociate={openAssociate}
                onEditMembership={openEditMembership}
                onDeleteMembership={openDeleteMembership}
              />
            ))}
          </div>

          <div className="hidden gap-4 md:grid md:grid-cols-[minmax(22rem,2fr)_3fr] md:items-start">
            <UserListTable
              users={filtered}
              selectedUserId={selectedUserId}
              onSelectUser={setSelectedUserId}
            />

            <UserDetailPanel
              key={selectedUserId ?? 'none'}
              user={selectedUser}
              onAssociate={openAssociate}
              onEditMembership={openEditMembership}
              onDeleteMembership={openDeleteMembership}
            />
          </div>
        </>
      )}

      <InviteUserDrawer
        state={inviteDrawer}
        onSubmit={(payload) => inviteMutation.mutateAsync(payload)}
      />

      <AssociateMembershipDrawer
        state={associateDrawer}
        user={associatingUser}
        onSubmit={(payload) => createMembershipMutation.mutateAsync(payload)}
      />

      <MembershipFormDrawer
        state={editMembershipDrawer}
        membership={editingMembership?.membership ?? null}
        user={editingMembership?.user ?? null}
        onSubmit={(membershipId, payload) =>
          updateMembershipMutation.mutateAsync({ membershipId, payload })
        }
      />

      <ConfirmDialog
        state={deleteModal}
        title="Remover associação"
        cancelLabel="Cancelar"
        confirmLabel="Remover"
        confirmVariant="danger"
        confirmDisabled={!deletingMembership}
        onConfirm={handleConfirmDelete}
        message={
          <p className="text-muted">
            Tem certeza que deseja remover a associação de{' '}
            <strong>
              {deletingMembership ? getUserDisplayName(deletingMembership.user) : 'usuário'}
            </strong>{' '}
            com <strong>{deletingMembership?.membership.condominiumName ?? 'condomínio'}</strong>?
          </p>
        }
      />
    </div>
  );
}
